import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getMedias } from '../../api/MediaAPI';
import { AppThunk } from '../../app/store';
import { OptionsType, OptionType } from '../../components/filterTypes/multiselectbox/types';
import { MediaDTO } from '../../dto/MediaDto';

import { updateURLQuery, getQueryArrayParam } from '../../components/QueryBuilder';

interface MediaState {
  key: string;
  label: string;
  type: string;
  values: MediaDTO[];
  isLoading: boolean;
  error: string | null;
  selectedIds: number[];
  selected: OptionsType | MediaDTO[];
  isCombobox: boolean;
  inputValue: string;
  isPageLoad: boolean;
  placeholder: string;
  econdaName: string;
  infoButtonDescription: string;
}

const initialState: MediaState = {
  key: 'medias',
  label: 'mediaFilter',
  type: 'multiSelectBox',
  values: [],
  isLoading: false,
  error: null,
  selectedIds: getQueryArrayParam('mediaIds', []),
  selected: [],
  isCombobox: true,
  inputValue: '',
  isPageLoad: true, //this parameter is used to know if we should load the selected options from the url
  placeholder: 'searchByMediaNameOrFormula',
  econdaName: 'media',
  infoButtonDescription: 'infoButtonMediaDescription',
};

function startLoading(state: MediaState) {
  state.isLoading = true;
}

function loadingFailed(state: MediaState, action: PayloadAction<string>) {
  state.isLoading = false;
  state.error = action.payload;
}

function updateSelectedMedias(state: MediaState, action: PayloadAction<OptionsType>) {
  state.selectedIds = [];
  state.selected = action.payload;
  action.payload.forEach((mediaSelected) => {
    state.selectedIds.push(mediaSelected.id);
  });
  updateURLQuery('mediaIds', state.selectedIds.map(String));
}

function updateInputData(state: MediaState, action: PayloadAction<string>) {
  state.inputValue = action.payload;
}

function getValueFromSelectedId(state: MediaState): void {
  if (state.isPageLoad) {
    state.selected = [];
    state.selectedIds.forEach((element) => {
      const selectedElement = state.values.find((value) => value.id === Number(element));
      if (selectedElement) {
        state.selectedIds.push(Number(selectedElement.id));
        state.selected.push(selectedElement);
      }
    });
    state.isPageLoad = false;
  }
}

export const mediasSlice = createSlice({
  name: 'medias',
  initialState,
  reducers: {
    getMediasStart: startLoading,
    getMediasSuccess(state, { payload }: PayloadAction<MediaDTO[]>) {
      state.values = payload;
      getValueFromSelectedId(state);
      state.isLoading = false;
      state.error = null;
    },
    getMediasFailure: loadingFailed,
    updateMediasSelected: updateSelectedMedias,
    updateMediasInput: updateInputData,
  },
});

export const { getMediasStart, getMediasSuccess, getMediasFailure, updateMediasSelected, updateMediasInput } =
  mediasSlice.actions;

export default mediasSlice.reducer;

export const fetchMedias =
  (query: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getMediasStart());
      const medias = await getMedias(query);
      dispatch(getMediasSuccess(medias));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(getMediasFailure(err.message));
      } else {
        dispatch(getMediasFailure("Can't dispatch getMedias"));
      }
    }
  };
